import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import API from '../services/api';

const STORAGE_KEY = 'app-theme';
const ThemeCtx = createContext({ theme: null, setTheme: () => {}, refreshTheme: async () => {} });

function setVar(name, value) {
  const r = document.documentElement;
  if (value == null || value === '') r.style.removeProperty(name);
  else r.style.setProperty(name, value);
}

export function applyTheme(theme) {
  if (!theme) {
    setVar('--header-color', null);
    setVar('--primary-color', null);
    setVar('--secondary-color', null);
    return;
  }
  setVar('--header-color', '#4A4A4A');
  setVar('--primary-color', theme.primary_color);
  setVar('--secondary-color', theme.secondary_color);
}

export function readTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveTheme(theme) {
  if (theme) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(theme)); } catch {}
  } else {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }
  applyTheme(theme);
}

export function resetTheme() {
  saveTheme(null);
}

export async function syncTheme() {
  try {
    const res = await API.get('/business/portal-theme');
    if (res?.data?.enabled) {
      const theme = res.data.theme ?? null;
      saveTheme(theme);
      return theme;
    }
  } catch {}
  resetTheme();
  return null;
}

export function useTheme() {
  return useContext(ThemeCtx);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readTheme());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    applyTheme(theme);
    (async () => {
      const fresh = await syncTheme();
      setThemeState(fresh);
      setReady(true);
    })();
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme(next) {
      saveTheme(next ?? null);
      setThemeState(next ?? null);
    },
    async refreshTheme() {
      const fresh = await syncTheme();
      setThemeState(fresh);
    }
  }), [theme]);

  if (!ready) return null;
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function pickTextColor(input) {
  let hex = (input || '').trim();
  if (hex.startsWith('var(')) {
    const varName = hex.slice(4, -1).trim();
    hex = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  }
  if (!hex) return '#fff';
  let h = hex.replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000' : '#fff';
}
